/**
 * Skeleton Component
 * Step 4 - Design System Refinement
 *
 * Enhanced skeleton loaders with multiple variants for different content types.
 * Uses the shimmer animation from globals.css for smooth loading states.
 */

import { cn } from "@/lib/utils";
import * as React from "react";

/* =================================================================
   BASE SKELETON
   ================================================================= */

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Animation variant
   * - 'shimmer': Moving gradient effect (default)
   * - 'pulse': Simple opacity pulse
   */
  variant?: "shimmer" | "pulse";
}
function Skeleton({
  className,
  variant = "shimmer",
  ...props
}: SkeletonProps) {
  return <div data-testid="skeleton" aria-busy="true" aria-label="Loading..." className={cn("rounded-md", variant === "shimmer" ? "animate-shimmer" : "animate-pulse bg-gradient-to-br from-input/20 via-input/50 to-input/5", className)} {...props} data-sentry-component="Skeleton" data-sentry-source-file="skeleton.tsx" />;
}

/* =================================================================
   CARD SKELETON
   For trading cards, portfolio cards, etc.
   ================================================================= */

interface CardSkeletonProps {
  showHeader?: boolean;
  showChart?: boolean;
  rows?: number;
  className?: string;
}
function CardSkeleton({
  showHeader = true,
  showChart = false,
  rows = 3,
  className
}: CardSkeletonProps) {
  return <div data-testid="card-skeleton" aria-busy="true" aria-label="Loading card content" className={cn("space-y-4 rounded-lg border border-card-border bg-card-bg p-4", className)} data-sentry-component="CardSkeleton" data-sentry-source-file="skeleton.tsx">
      {/* Header */}
      {showHeader && <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>}

      {/* Chart placeholder */}
      {showChart && <Skeleton className="h-32 w-full rounded-md" />}

      {/* Content rows */}
      <div className="space-y-3">
        {Array.from({
        length: rows
      }).map((_, i) => <div key={i} className="flex items-center justify-between">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-1/4" />
          </div>)}
      </div>
    </div>;
}

/* =================================================================
   TABLE SKELETON
   For data tables, order lists, etc.
   ================================================================= */

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  className?: string;
}
function TableSkeleton({
  rows = 5,
  columns = 4,
  showHeader = true,
  className
}: TableSkeletonProps) {
  return <div data-testid="table-skeleton" aria-busy="true" aria-label="Loading table data" className={cn("w-full", className)} data-sentry-component="TableSkeleton" data-sentry-source-file="skeleton.tsx">
      {/* Table Header */}
      {showHeader && <div className="mb-3 flex gap-4 border-b border-card-border pb-3">
          {Array.from({
        length: columns
      }).map((_, i) => <Skeleton key={`header-${i}`} className="h-4 flex-1" style={{
        maxWidth: i === 0 ? "30%" : "20%"
      }} />)}
        </div>}

      {/* Table Rows */}
      <div className="space-y-3">
        {Array.from({
        length: rows
      }).map((_, rowIndex) => <div key={rowIndex} className="flex items-center gap-4">
            {Array.from({
          length: columns
        }).map((_, colIndex) => <Skeleton key={`cell-${rowIndex}-${colIndex}`} className="h-4 flex-1" style={{
          maxWidth: colIndex === 0 ? "30%" : "20%"
        }} />)}
          </div>)}
      </div>
    </div>;
}

/* =================================================================
   CHART SKELETON
   For TradingView charts, area charts, etc.
   ================================================================= */

interface ChartSkeletonProps {
  height?: number;
  showLegend?: boolean;
  className?: string;
}
function ChartSkeleton({
  height = 300,
  showLegend = true,
  className
}: ChartSkeletonProps) {
  return <div data-testid="chart-skeleton" aria-busy="true" aria-label="Loading chart" className={cn("w-full", className)} data-sentry-component="ChartSkeleton" data-sentry-source-file="skeleton.tsx">
      {/* Legend */}
      {showLegend && <div className="mb-4 flex gap-4">
          {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-2">
              <Skeleton className="h-3 w-3 rounded-full" />
              <Skeleton className="h-3 w-16" />
            </div>)}
        </div>}

      {/* Chart area with fake chart bars */}
      <div className="relative overflow-hidden rounded-md bg-input/10" style={{
      height
    }}>
        {/* Y-axis labels */}
        <div className="absolute bottom-0 left-2 top-0 flex flex-col justify-between py-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-3 w-8" />)}
        </div>

        {/* Chart bars */}
        <div className="absolute bottom-8 left-14 right-4 top-4 flex items-end gap-1">
          {Array.from({
          length: 20
        }).map((_, i) => <Skeleton key={i} className="flex-1 rounded-t" style={{
          height: `${Math.random() * 60 + 20}%`,
          animationDelay: `${i * 50}ms`
        }} />)}
        </div>

        {/* X-axis labels */}
        <div className="absolute bottom-0 left-14 right-4 flex justify-between py-1">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-3 w-8" />)}
        </div>
      </div>
    </div>;
}

/* =================================================================
   CONVERSATION SKELETON
   For chat messages, AI responses, etc.
   ================================================================= */

interface ConversationSkeletonProps {
  messages?: number;
  className?: string;
}
function ConversationSkeleton({
  messages = 3,
  className
}: ConversationSkeletonProps) {
  return <div data-testid="conversation-skeleton" aria-busy="true" aria-label="Loading conversation" className={cn("space-y-4", className)} data-sentry-component="ConversationSkeleton" data-sentry-source-file="skeleton.tsx">
      {Array.from({
      length: messages
    }).map((_, i) => {
      const isUser = i % 2 === 0;
      return <div key={i} className={cn("flex gap-3", isUser ? "justify-end" : "justify-start")}>
            {!isUser && <Skeleton className="h-8 w-8 flex-shrink-0 rounded-full" />}
            <div className={cn("space-y-2", isUser ? "items-end" : "items-start", "flex flex-col")}>
              <Skeleton className={cn("h-4 rounded", isUser ? "w-32" : "w-48")} />
              <Skeleton className={cn("h-4 rounded", isUser ? "w-24" : "w-64")} />
              {!isUser && <Skeleton className="h-4 w-40 rounded" />}
            </div>
            {isUser && <Skeleton className="h-8 w-8 flex-shrink-0 rounded-full" />}
          </div>;
    })}
    </div>;
}

/* =================================================================
   KPI CARD SKELETON
   For dashboard KPI/stat cards
   ================================================================= */

interface KPICardSkeletonProps {
  showIcon?: boolean;
  showTrend?: boolean;
  className?: string;
}
function KPICardSkeleton({
  showIcon = true,
  showTrend = true,
  className
}: KPICardSkeletonProps) {
  return <div data-testid="kpi-card-skeleton" aria-busy="true" aria-label="Loading KPI data" className={cn("rounded-lg border border-card-border bg-card-bg p-4", className)} data-sentry-component="KPICardSkeleton" data-sentry-source-file="skeleton.tsx">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <Skeleton className="h-3 w-20" data-sentry-element="Skeleton" data-sentry-source-file="skeleton.tsx" />
          <Skeleton className="h-8 w-28" data-sentry-element="Skeleton" data-sentry-source-file="skeleton.tsx" />
          {showTrend && <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-3 w-16" />
            </div>}
        </div>
        {showIcon && <Skeleton className="h-10 w-10 rounded-lg" />}
      </div>
    </div>;
}

/* =================================================================
   DASHBOARD SKELETON
   Full dashboard layout skeleton
   ================================================================= */

interface DashboardSkeletonProps {
  className?: string;
}
function DashboardSkeleton({
  className
}: DashboardSkeletonProps) {
  return <div data-testid="dashboard-skeleton" aria-busy="true" aria-label="Loading dashboard" className={cn("space-y-6", className)} data-sentry-component="DashboardSkeleton" data-sentry-source-file="skeleton.tsx">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" data-sentry-element="Skeleton" data-sentry-source-file="skeleton.tsx" />
          <Skeleton className="h-4 w-32" data-sentry-element="Skeleton" data-sentry-source-file="skeleton.tsx" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-10 w-32 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="skeleton.tsx" />
          <Skeleton className="h-10 w-10 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="skeleton.tsx" />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map(i => <KPICardSkeleton key={i} />)}
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <CardSkeleton showChart rows={0} className="h-[400px]" data-sentry-element="CardSkeleton" data-sentry-source-file="skeleton.tsx" />
        </div>
        <div>
          <CardSkeleton rows={5} data-sentry-element="CardSkeleton" data-sentry-source-file="skeleton.tsx" />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-card-border bg-card-bg p-4">
        <Skeleton className="mb-4 h-6 w-32" data-sentry-element="Skeleton" data-sentry-source-file="skeleton.tsx" />
        <TableSkeleton rows={5} columns={5} data-sentry-element="TableSkeleton" data-sentry-source-file="skeleton.tsx" />
      </div>
    </div>;
}

/* =================================================================
   LIST SKELETON
   For simple lists
   ================================================================= */

interface ListSkeletonProps {
  items?: number;
  showAvatar?: boolean;
  className?: string;
}
function ListSkeleton({
  items = 5,
  showAvatar = false,
  className
}: ListSkeletonProps) {
  return <div data-testid="list-skeleton" aria-busy="true" aria-label="Loading list" className={cn("space-y-3", className)} data-sentry-component="ListSkeleton" data-sentry-source-file="skeleton.tsx">
      {Array.from({
      length: items
    }).map((_, i) => <div key={i} className="flex items-center gap-3">
          {showAvatar && <Skeleton className="h-10 w-10 rounded-full" />}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>)}
    </div>;
}

/* =================================================================
   EXPORTS
   ================================================================= */

export { CardSkeleton, ChartSkeleton, ConversationSkeleton, DashboardSkeleton, KPICardSkeleton, ListSkeleton, Skeleton, TableSkeleton };